import React, { useState, useRef, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperPlane,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import BotAvatar from "./BotAvatar";
import { useTranslation } from "react-i18next";
import { useChatBotStore } from "../../store/ChatBotStore";
import MessageBubble from "./MessageBubble";

const ChatBotMain = () => {
  const { t } = useTranslation("translations");
  const {
    messages,
    isLoading,
    sendMessage,
    closeChat,
    activeConversationId,
    conversations,
  } = useChatBotStore();

  const [inputValue, setInputValue] = useState("");
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const isPreviousConversation = useMemo(() => {
    if (!activeConversationId) return false;
    const currentConversation = conversations.find(c => c.id === activeConversationId);
    return currentConversation?.isPreviousConversation || false;
  }, [activeConversationId, conversations]);

  const hasQuickReplies = useMemo(() => {
    if (messages.length === 0) return false;
    const lastMessage = messages[messages.length - 1];
    return lastMessage?.type === "bot" &&
           Array.isArray(lastMessage?.content?.quickReplies) &&
           lastMessage.content.quickReplies.length > 0;
  }, [messages]);

  const suggestions = useMemo(() => [
    { label: t("chatbot.suggestionPurchaseInvoices"), query: "Me trego faturat e blerjes" },
    { label: t("chatbot.suggestionSalesInvoices"), query: "Me trego faturat e shitjes" },
    { label: t("chatbot.suggestionUnpaidInvoices"), query: "faturat e papaguara" },
    { label: t("chatbot.suggestionVatTurnover"), query: "Xhiro dhe TVSH për muajin aktual" },
{ label: t("chatbot.suggestionSubscription"), query: "Cfare abonimi ka kompania?" },
    { label: t("chatbot.suggestionCorrectedInvoices"), query: "faturat korrigjuese" },
  ], [t]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [activeConversationId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim() && !isLoading) {
      sendMessage(inputValue.trim());
      setInputValue("");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="chatbot-main">
      <div className="chatbot-header">
        <div className="header-title">
          <BotAvatar size={38} className="bot-icon" />
          <span>{t("chatbot.chatAssistant")}</span>
        </div>
        <button className="close-btn" onClick={closeChat} title={t("chatbot.closeChat")}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <div className="messages-container">
        {messages.length === 0 ? (
          <div className="welcome-message">
            <div className="welcome-icon">
              <BotAvatar size={110} showBg />
            </div>
            <h2>{t("chatbot.welcomeTitle")}</h2>
            <p>
              {t("chatbot.welcomeMessage")}
            </p>
            {!isPreviousConversation && (
              <div className="suggestion-chips">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => sendMessage(suggestion.query)}
                  >
                    {suggestion.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
            {isLoading && (
              <div className="message-bubble bot loading">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {!isPreviousConversation && !hasQuickReplies && (
        <form className="input-container" onSubmit={handleSubmit}>
          <div className="input-wrapper">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t("chatbot.typeMessage")}
              rows={1}
              disabled={isLoading}
            />
            <button
              type="submit"
              className="send-btn"
              disabled={!inputValue.trim() || isLoading}
              title={t("chatbot.sendMessage")}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ChatBotMain;
